/* eslint-disable no-throw-literal */
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery } from 'react-query';
import {
  PAS_STATUS,
  POLL_RETRIES,
  PAYMENT_METHODS_POLL_RETRIES,
  PAS_FAILURE_ERRORS,
  SET_PAYMENT_SHARING_RETRIES,
  getPollRetryDelay,
  getPaymentMethodsPollRetryDelay,
  getSetPaymentSharingRetryDelay,
} from 'constants/paymentsSpa';
import paymentsSpaActions from 'redux/paymentsSpa/actions';
import { ddLogWithContext } from 'utils/errorLoggerUtil';
import useTranslation from 'utils/hooks/useTranslation';
import notify, { notifyErrorWithWorkflowId } from 'utils/notificationUtils';
import paymentsSpa from '../api/paymentsSpa';
import { paymentQueryKeys } from './queryKeys';
import useOnboardingProgressBar from 'components/Shared/OnboardingProgressBar/useOnboardingProgressBar';
import { getMaximumPaymentAccounts } from 'constants.js';
import { CORPORATE_CLIENT_RESTRICTION_TYPES } from 'constants/corporateClients';
import { isSuccess } from 'utils/sagasUtil';

const usePollPayments = ({ id, workflowId }) => {
  const { translateText } = useTranslation();
  const { onGetOnboardingProgress } = useOnboardingProgressBar();
  const [orderInitiated, setOrderInitiated] = React.useState('');
  const [scaChallenge, setScaChallenge] = React.useState('');

  React.useEffect(() => {
    if (orderInitiated) {
      ddLogWithContext({
        type: 'info',
        message: `Payment creation status changed (${PAS_STATUS.InitiateAuthentication}). Attaching hidden iframe.`,
        context: {
          id,
          workflowId,
        },
      });
    }
  }, [id, workflowId, orderInitiated]);

  React.useEffect(() => {
    if (scaChallenge) {
      ddLogWithContext({
        type: 'info',
        message: `Payment creation status changed (${PAS_STATUS.Challenged}). Attaching challenge iframe.`,
        context: {
          id,
          workflowId,
        },
      });
    }
  }, [id, workflowId, scaChallenge]);

  const pollPayments = React.useCallback(
    async ({ queryKey }) => {
      const [, paymentId, traceId] = queryKey;
      const { data } = await paymentsSpa.readOne(paymentId, traceId);
      const { creationStatus } = data;
      if (creationStatus.status === PAS_STATUS.Pending) {
        throw {
          status: PAS_STATUS.Pending,
          paymentId,
          workflowId: traceId,
        };
      }

      const { cardValidations } = creationStatus;
      if (creationStatus.status === PAS_STATUS.InitiateAuthentication) {
        const orderInitiated = cardValidations[0].initiateAuthentication.gatewayHtml;
        setOrderInitiated(atob(orderInitiated));
        throw {
          status: PAS_STATUS.InitiateAuthentication,
          paymentId,
          workflowId: traceId,
        };
      }

      if (creationStatus.status === PAS_STATUS.Challenged) {
        const challenge = cardValidations[0].challengeDetails.challengeQuestion;
        setScaChallenge(atob(challenge));
        throw {
          status: PAS_STATUS.Challenged,
          paymentId,
          workflowId: traceId,
        };
      }

      if (creationStatus.status === PAS_STATUS.Failed) {
        const reason =
          PAS_FAILURE_ERRORS[creationStatus.failureReason] || PAS_FAILURE_ERRORS.UnhandledError;
        ddLogWithContext({
          type: 'warn',
          message: `Payment creation failed with reason ${reason}. Exiting polling process.`,
          context: {
            id: paymentId,
            workflowId: traceId,
          },
        });
        throw {
          status: PAS_STATUS.Failed,
          reason,
          paymentId,
          workflowId: traceId,
        };
      }

      if (creationStatus.status === PAS_STATUS.Created) {
        ddLogWithContext({
          type: 'info',
          message: `Payment creation successful. Exiting polling process.`,
          context: {
            id: paymentId,
            workflowId: traceId,
          },
        });
        return {
          status: PAS_STATUS.Created,
          paymentId,
          workflowId: traceId,
        };
      }

      ddLogWithContext({
        type: 'warn',
        message: 'Payment polling process could not continue due to an unhandled error',
        context: {
          id: paymentId,
          workflowId: traceId,
        },
      });
      throw {
        status: PAS_STATUS.Failed,
        reason: PAS_FAILURE_ERRORS.UnhandledError,
        paymentId,
        workflowId: traceId,
      };
    },
    [setOrderInitiated, setScaChallenge],
  );

  const query = useQuery({
    queryFn: pollPayments,
    queryKey: ['pollPayments', id, workflowId],
    retry: (retryCount, error) => {
      if (retryCount > POLL_RETRIES) {
        ddLogWithContext({
          type: 'info',
          message: 'Payment polling process expired.',
          context: {
            id: error.paymentId,
            workflowId: error.workflowId,
          },
        });
        return false;
      }

      const status = error?.status;
      if (
        status === PAS_STATUS.Pending ||
        status === PAS_STATUS.InitiateAuthentication ||
        status === PAS_STATUS.Challenged
      ) {
        return true;
      }

      return false;
    },
    retryDelay: getPollRetryDelay,
    enabled: false,
    onSuccess: () => {
      notify({
        type: 'success',
        message: translateText('corporateAccounts.payments.create.success'),
      });
      onGetOnboardingProgress();
    },
    onError: error => {
      const mappedError = error.reason || PAS_FAILURE_ERRORS.UnhandledError;

      notifyErrorWithWorkflowId({
        errorCodeTitle: translateText('corporateAccounts.common.errorCode'),
        message: `${translateText(
          `corporateAccounts.payments.create.error.${mappedError}`,
        )} ${translateText('corporateAccounts.common.errorCode.copy')}`,
        workflowId: error.workflowId,
      });
    },
  });

  const pollReset = React.useCallback(() => {
    query.remove();
    setScaChallenge('');
    setOrderInitiated('');
  }, [setOrderInitiated, setScaChallenge, query]);

  return {
    startPolling: query.refetch,
    isPollSuccess: query.isSuccess,
    isPollError: query.isError,
    isPolling: query.isLoading,
    pollError: query.error,
    scaChallenge,
    orderInitiated,
    pollReset,
  };
};

// The payment methods list (corporate-accounts) lags behind PAS: right after a card is created, the
// first GET /payments often comes back without it. Re-read the list until the new payment account
// shows up, then hand the result to the paymentsSpa store the legacy list is rendered from.
export const useRefreshPaymentMethods = () => {
  const dispatch = useDispatch();
  const [target, setTarget] = React.useState({ paymentAccountId: null, sharedWithDrivers: null });
  const { paymentAccountId, sharedWithDrivers } = target;

  const readPaymentMethods = React.useCallback(async ({ queryKey }) => {
    const [, createdPaymentAccountId, expectedSharing] = queryKey;
    const { response } = await paymentsSpa.read();
    const paymentMethods = response?.data || [];

    const createdCard = paymentMethods.find(
      paymentMethod =>
        paymentMethod.paymentAccountId?.toLowerCase() === createdPaymentAccountId?.toLowerCase(),
    );

    // The sharing flag is polled for as well as the card: a list read before the PATCH lands shows
    // the card still available to drivers.
    if (
      !createdCard ||
      (expectedSharing !== null && createdCard.sharedWithDrivers !== expectedSharing)
    ) {
      throw {
        paymentAccountId: createdPaymentAccountId,
        paymentMethods,
      };
    }

    return paymentMethods;
  }, []);

  const query = useQuery({
    queryFn: readPaymentMethods,
    queryKey: [paymentQueryKeys.getPaymentMethodsQuery, paymentAccountId, sharedWithDrivers],
    enabled: false,
    cacheTime: 0,
    retry: retryCount => retryCount < PAYMENT_METHODS_POLL_RETRIES,
    retryDelay: getPaymentMethodsPollRetryDelay,
    onSuccess: paymentMethods => {
      dispatch({ type: paymentsSpaActions.GET_SUCCESS, payload: paymentMethods });
    },
    onError: error => {
      ddLogWithContext({
        type: 'warn',
        message: 'Payment account is still missing from the payment methods list. Giving up.',
        context: {
          id: error?.paymentAccountId,
          retries: PAYMENT_METHODS_POLL_RETRIES,
        },
      });

      // The list itself was readable, it just does not hold the new card yet — show what it returned
      // rather than leaving the Select spinning or in an error state.
      if (error?.paymentMethods) {
        dispatch({ type: paymentsSpaActions.GET_SUCCESS, payload: error.paymentMethods });
      } else {
        dispatch({ type: paymentsSpaActions.GET_ERROR });
      }
    },
  });

  const { refetch } = query;

  React.useEffect(() => {
    if (paymentAccountId) {
      dispatch({ type: paymentsSpaActions.SET_STATE, payload: { loading: true, getError: false } });
      refetch();
    }
  }, [paymentAccountId, dispatch, refetch]);

  const refreshPaymentMethods = React.useCallback(
    (createdPaymentAccountId, expectedSharing = null) =>
      setTarget({ paymentAccountId: createdPaymentAccountId, sharedWithDrivers: expectedSharing }),
    [],
  );

  return {
    refreshPaymentMethods,
    isRefreshingPaymentMethods: query.isFetching,
  };
};

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

// Removes a card that could not be taken away from the drivers. Returns whether the card is gone.
const useDiscardPaymentAccount = () => {
  const dispatch = useDispatch();

  return React.useCallback(
    async paymentAccountId => {
      try {
        // destroy takes the corporate-accounts row id, which the PAS creation response never carries,
        // and GET /payments lags PAS. Reading the list once gives up in the window the sharing call
        // is fired in, which leaves a card the user excluded shared with every driver, so the card
        // is polled for on the same staircase the rest of this file uses.
        let card;

        for (let attempt = 0; attempt <= PAYMENT_METHODS_POLL_RETRIES; attempt += 1) {
          if (attempt > 0) {
            await wait(getPaymentMethodsPollRetryDelay(attempt - 1));
          }

          const { response } = await paymentsSpa.read();
          card = (response?.data || []).find(
            paymentMethod =>
              paymentMethod.paymentAccountId?.toLowerCase() === paymentAccountId?.toLowerCase(),
          );

          if (card) break;
        }

        if (!card) {
          // Corporate-accounts still has not learned about the card, so there is nothing to delete
          // and nothing keeping it from being shared once it does.
          return false;
        }

        const { response: deleteResponse } = await paymentsSpa.destroy(card.id);

        if (!isSuccess(deleteResponse)) return false;

        dispatch({ type: paymentsSpaActions.GET });
        return true;
      } catch (error) {
        return false;
      }
    },
    [dispatch],
  );
};

// Corporate-accounts accepts this before or after it learns about the card from PAS, and repeating
// it for the same card is harmless. The caller owns what happens when it fails, because a card
// whose sharing could not be changed is still available to drivers.
export const useSetPaymentSharing = () => {
  return useMutation({
    mutationFn: async ({ paymentAccountId, sharedWithDrivers }) => {
      const result = await paymentsSpa.setSharing({ paymentAccountId, sharedWithDrivers });

      // httpWrapper returns instead of throwing when it signs the user out on a 401, which would
      // otherwise be recorded as sharing that was changed.
      if (!result?.status) {
        throw Object.assign(new Error('setSharing did not complete'), { noRetry: true });
      }

      return result.data;
    },
    mutationKey: 'setPaymentSharing',
    retry: (failureCount, error) => {
      if (failureCount >= SET_PAYMENT_SHARING_RETRIES || error?.noRetry) return false;

      // No response at all means the request never landed, which is as worth repeating as a 5xx.
      const status = error?.response?.status;
      return status === undefined || status >= 500;
    },
    retryDelay: getSetPaymentSharingRetryDelay,
    onError: (error, { paymentAccountId, sharedWithDrivers }) => {
      ddLogWithContext({
        type: 'error',
        message: `Failed to set sharedWithDrivers to ${sharedWithDrivers} for a payment account.`,
        context: {
          id: paymentAccountId,
          workflowId: error?.response?.data?.workflowId,
          status: error?.response?.status,
        },
      });
    },
  });
};

// A card is shared with the corporate client's drivers unless the form said otherwise, which is the
// backend default. `sharedWithDrivers: false` is sent as soon as PAS returns the card's id, without
// waiting for the card to finish being created.
export const useCreateSharedPayment = ({ onReset, sharedWithDrivers = true }) => {
  const { translateText } = useTranslation();
  const sharingMutation = useSetPaymentSharing();
  const { mutate: setSharing } = sharingMutation;
  const discardPaymentAccount = useDiscardPaymentAccount();

  // A card the drivers could not be kept away from is deleted rather than left available to them.
  // The user is told either way, because the card they asked for is gone.
  const handleSharingError = React.useCallback(
    async (error, paymentAccountId) => {
      const workflowId = error?.response?.data?.workflowId;
      const discarded = await discardPaymentAccount(paymentAccountId);

      if (!discarded) {
        ddLogWithContext({
          type: 'error',
          message: 'Failed to remove a payment account whose sharing could not be set.',
          context: { id: paymentAccountId },
        });
      }

      notifyErrorWithWorkflowId({
        errorCodeTitle: translateText('corporateAccounts.common.errorCode'),
        message: `${translateText(
          discarded
            ? 'corporateAccounts.payments.setSharing.error.cardRemoved'
            : 'corporateAccounts.payments.setSharing.error.cardKept',
        )} ${translateText('corporateAccounts.common.errorCode.copy')}`,
        workflowId,
      });
    },
    [discardPaymentAccount, translateText],
  );
  const [pollingDetails, setPollingDetails] = React.useState({ id: null, workflowId: null });
  const {
    startPolling,
    isPolling,
    isPollSuccess,
    isPollError,
    pollError,
    scaChallenge,
    orderInitiated,
    pollReset,
  } = usePollPayments(pollingDetails);
  const createPayment = async ({ card, restrictionType }) => {
    if (restrictionType?.toLowerCase() === CORPORATE_CLIENT_RESTRICTION_TYPES.RESTRICTED) {
      const {
        response: { data: availablePaymentMethods },
      } = await paymentsSpa.read();
      if (availablePaymentMethods.length >= getMaximumPaymentAccounts())
        throw {
          status: PAS_STATUS.Failed,
          reason: PAS_FAILURE_ERRORS.RestrictedCorporateClientTotalLimitExceeded,
        };
    }

    const { response, workflowId } = await paymentsSpa.create(card);
    return {
      data: response?.data,
      workflowId,
    };
  };

  useEffect(() => {
    if (pollingDetails.id && pollingDetails.workflowId) {
      ddLogWithContext({
        type: 'info',
        message: `Payment creation initiated. Starting polling process for payment ID ${pollingDetails.id}.`,
        context: {
          ...pollingDetails,
        },
      });
      startPolling();
    }
  }, [pollingDetails, startPolling]);

  useEffect(() => {
    return () => {
      setPollingDetails({ id: null, workflowId: null });
    };
  }, [setPollingDetails]);

  const mutation = useMutation({
    mutationFn: createPayment,
    mutationKey: 'createPayment',
    onSuccess: ({ data, workflowId }) => {
      const { paymentAccountId } = data.creationStatus;
      setPollingDetails({
        id: paymentAccountId,
        workflowId,
      });

      if (!sharedWithDrivers) {
        setSharing(
          { paymentAccountId, sharedWithDrivers: false },
          { onError: error => handleSharingError(error, paymentAccountId) },
        );
      }
    },
    onError: error => {
      const mappedError = error.reason || PAS_FAILURE_ERRORS.UnhandledError;

      notifyErrorWithWorkflowId({
        errorCodeTitle: translateText('corporateAccounts.common.errorCode'),
        message: `${translateText(
          `corporateAccounts.payments.create.error.${mappedError}`,
        )} ${translateText('corporateAccounts.common.errorCode.copy')}`,
        workflowId: error.workflowId,
      });
    },
  });
  // The card is not finished while it is still available to drivers, so a caller that reads the
  // card list on success is held until the sharing call settles rather than racing it.
  const isSharingSettled =
    sharedWithDrivers || sharingMutation.isSuccess || sharingMutation.isError;

  return {
    ...mutation,
    reset: () => {
      mutation.reset();
      sharingMutation.reset();
      pollReset();
      onReset();
    },
    isLoading: mutation.isLoading || isPolling || (isPollSuccess && !isSharingSettled),
    isSuccess: isPollSuccess && isSharingSettled,
    // The card is being deleted, so there is nothing left to read the list for
    isSharingError: sharingMutation.isError,
    paymentAccountId: pollingDetails.id,
    isError: isPollError,
    pollError,
    scaChallenge,
    orderInitiated,
  };
};
